import { ref, onBeforeUnmount, type Ref } from "vue";
import * as Cesium from "cesium";

export function useMemoryTracking(
    viewer: Ref<Cesium.Viewer | null>,
    google3DTileset: Ref<Cesium.Cesium3DTileset | null>,
    jakartoPointclouds: Ref<Cesium.Cesium3DTileset | null>,
    aerialPointclouds: Ref<Cesium.Cesium3DTileset | null>
) {
    const memoryUsage = ref<number>(0);
    const memoryUpdateInterval = ref<number | null>(null);

    const updateMemoryUsage = (): void => {
        if (!viewer.value) return;

        try {
            let totalMemoryUsage = 0;

            // Get memory usage from the Google 3D Tileset
            if (
                google3DTileset.value &&
                google3DTileset.value.totalMemoryUsageInBytes
            ) {
                totalMemoryUsage += google3DTileset.value.totalMemoryUsageInBytes;
            }

            // Get memory usage from the Jakarto pointclouds
            if (jakartoPointclouds.value) {
              totalMemoryUsage += jakartoPointclouds.value.totalMemoryUsageInBytes;
            }
            if (aerialPointclouds.value) {
              totalMemoryUsage += aerialPointclouds.value.totalMemoryUsageInBytes;
            }

            memoryUsage.value = totalMemoryUsage;
        } catch (error) {
            console.warn("Could not get memory usage:", error);
        }
    };

    const startMemoryTracking = (): void => {
        if (memoryUpdateInterval.value) return;

        // Update immediately
        updateMemoryUsage();

        // Update every 1000ms (1 second)
        memoryUpdateInterval.value = window.setInterval(updateMemoryUsage, 1000);
    };

    const stopMemoryTracking = (): void => {
        if (memoryUpdateInterval.value) {
            clearInterval(memoryUpdateInterval.value);
            memoryUpdateInterval.value = null;
        }
    };

    // Format memory usage for display
    const formatMemoryUsage = (bytes: number): string => {
        if (bytes === 0) return "0 B";

        const units = ["B", "KB", "MB", "GB"];
        let size = bytes;
        let unitIndex = 0;

        while (size >= 1024 && unitIndex < units.length - 1) {
            size /= 1024;
            unitIndex++;
        }

        return `${size.toFixed(1)} ${units[unitIndex]}`;
    };

    // Cleanup on unmount
    onBeforeUnmount(() => {
        stopMemoryTracking();
    });

    return {
        memoryUsage,
        startMemoryTracking,
        stopMemoryTracking,
        formatMemoryUsage,
    };
}
